'use client';

import { useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetTitle } from '~/components/ui/sheet';
import { cn } from '~/lib/utils';

type OverlayProps = {
  children: React.ReactNode;
  itemId: string | undefined | null;
  onClose: () => void;
  /** Kept for API compatibility; Sheet uses a fixed responsive width. */
  size?: string | number;
  className?: string;
};

const PORTALED_MENU_SELECTOR = [
  '[data-radix-select-content]',
  '[data-radix-popper-content-wrapper]',
  '[data-radix-menu-content]',
  '[data-radix-dropdown-menu-content]',
  '[data-slot="select-content"]',
  '[data-slot="dropdown-menu-content"]',
  '[data-slot="popover-content"]',
  '[data-policy-snapshot-inspector]',
  '.MuiPopover-root',
  '.MuiMenu-list',
  '[role="listbox"]',
  '[role="menu"]',
].join(', ');

/** Page chrome that should not dismiss the drawer (always in the DOM). */
const OUTSIDE_CLICK_IGNORE_SELECTOR = [
  '[data-feature-page-header]',
  '[data-filter-menu]',
  '.filter-menu',
].join(', ');

function isPortaledMenuTarget(el: Element): boolean {
  return Boolean(
    el.closest(`${PORTALED_MENU_SELECTOR}, ${OUTSIDE_CLICK_IGNORE_SELECTOR}, [role="option"]`),
  );
}

/** True while a portaled select/menu is open (still in the DOM on dismiss pointerdown). */
function isPortaledMenuOpen(): boolean {
  return Boolean(document.querySelector(PORTALED_MENU_SELECTOR));
}

/**
 * Right-side detail overlay. Keeps itemId/onClose API; Sheet under the hood.
 */
export default function Overlay(props: OverlayProps) {
  const { itemId, onClose, children, className } = props;
  const open = !!itemId;
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openRef.current) onClose();
    };
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, [onClose]);

  return (
    <Sheet
      open={open}
      modal={false}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        showOverlay={false}
        className={cn(
          // Inset to the content pane (layout py-2 pr-2). Covers the sticky
          // feature header; a definite height lets DrawerShell/h-full scroll.
          'inset-y-2 right-2 left-auto h-[calc(100vh-1rem)] w-[60vw] max-w-none sm:max-w-none',
          'gap-0 min-h-0 overflow-hidden p-0',
          'border-border/85 rounded-[var(--panel-radius)] border',
          'shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_8px_28px_-6px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.04)]',
          '[&>button]:hidden',
          className,
        )}
        onInteractOutside={(e) => {
          const target = e.target;
          const el =
            target instanceof Element
              ? target
              : target instanceof Node
                ? target.parentElement
                : null;
          if (!el) return;

          // Portaled Radix/MUI menus render outside the sheet DOM tree
          if (isPortaledMenuTarget(el)) {
            e.preventDefault();
            return;
          }

          // Clicking outside an open dropdown dismisses the menu first — don't
          // also close the drawer on that same gesture.
          if (isPortaledMenuOpen()) {
            e.preventDefault();
            return;
          }

          // Match prior Overlay: ignore link clicks for outside dismiss
          if (el.closest('a')) {
            e.preventDefault();
          }
        }}
      >
        <SheetTitle className="sr-only">Details</SheetTitle>
        {children}
      </SheetContent>
    </Sheet>
  );
}
